import { Eye, EyeOff, Lock, Mail, Phone, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../../utils/axios";

const UserLoginModal = ({ isOpen, setIsOpen }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState("login"); // "login", "register", "forgotPassword"
  const [step, setStep] = useState(1); // 1 = enter email, 2 = password/register
  const [isExistingUser, setIsExistingUser] = useState(null);
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
    confirmPassword: "",
    name: "",
    email: "",
    phoneNumber: "",
    newPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Prevent background scroll when modal open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isOpen]);
  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (mode === "login") {
      if (!formData.identifier)
        newErrors.identifier = "Email or phone is required";
      if (!formData.password) newErrors.password = "Password is required";
    } else if (mode === "register") {
      if (!formData.name) newErrors.name = "Name is required";
      if (!formData.email) newErrors.email = "Email is required";
      if (!formData.phoneNumber)
        newErrors.phoneNumber = "Phone number is required";
      if (!formData.password) newErrors.password = "Password is required";
    } else if (mode === "forgotPassword") {
      if (!formData.email) newErrors.email = "Email is required";
      if (!formData.phoneNumber)
        newErrors.phoneNumber = "Phone number is required";
      if (!formData.newPassword)
        newErrors.newPassword = "New password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // ================= LOGIN STEP 1 =================
      if (mode === "login" && step === 1) {
        if (!formData.identifier) {
          setErrors({ identifier: "Email or phone is required" });
          return;
        }

        const res = await axiosInstance.post("/users/check-user", {
          identifier: formData.identifier,
        });

        if (res.data.isExistingUser) {
          setStep(2);
        }
      }

      // ================= LOGIN STEP 2 =================
      else if (mode === "login" && step === 2) {
        if (!formData.password) {
          setErrors({ password: "Password is required" });
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setErrors({ confirmPassword: "Passwords do not match" });
          return;
        }
        const res = await axiosInstance.post("/users/login", {
          identifier: formData.identifier,
          password: formData.password,
        });

        localStorage.setItem("ngo-afsa-token", res.data.token);
        localStorage.setItem("ngo-afsa-data", JSON.stringify(res.data.user));

        Swal.fire("Success", "Login successful", "success");

        setIsOpen(false);
        window.location.reload();
      }

      // ================= REGISTER =================
      else if (mode === "register") {
        if (
          !formData.name ||
          !formData.email ||
          !formData.phoneNumber ||
          !formData.password
        ) {
          setErrors({
            name: !formData.name && "Name is required",
            email: !formData.email && "Email is required",
            phoneNumber: !formData.phoneNumber && "Phone is required",
            password: !formData.password && "Password is required",
          });
          return;
        }

        const res = await axiosInstance.post("/users/register", {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          password: formData.password,
        });

        Swal.fire("Success", res.data.message, "success");

        setMode("login");
        setStep(1);
        resetForm();
      }

      // ================= FORGOT PASSWORD =================
      else if (mode === "forgotPassword") {
        if (!formData.phoneNumber || !formData.newPassword) {
          setErrors({
            phoneNumber: !formData.phoneNumber && "Phone is required",
            newPassword: !formData.newPassword && "New password required",
          });
          return;
        }

        const res = await axiosInstance.post("/users/forgot-password", {
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          newPassword: formData.newPassword,
        });

        Swal.fire("Success", res.data.message, "success");

        setMode("login");
        setStep(1);
        resetForm();
      }
    } catch (error) {
      console.log(error);

      if (mode === "login" && step === 1 && error.response?.status === 404) {
        Swal.fire("New User", "No account found. Please register.", "info");

        setMode("register");

        setFormData((prev) => ({
          ...prev,
          email: prev.identifier.includes("@") ? prev.identifier : "",
          phoneNumber: !prev.identifier.includes("@") ? prev.identifier : "",
        }));
      } else {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };
  const resetForm = () => {
    setFormData({
      identifier: "",
      password: "",
      confirmPassword: "",
      name: "",
      email: "",
      phoneNumber: "",
      userType: "",
      gender: "",
      dateOfBirth: "",
      address: "",
      nationality: "",
      newPassword: "",
    });
    setErrors({});
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  if (!isOpen) return null;

  const getModalTitle = () => {
    switch (mode) {
      case "login":
        return "Welcome Back";
      case "register":
        return "Create Your Account ";
      case "forgotPassword":
        return "Reset Password 🔑";
      default:
        return "Welcome";
    }
  };

  const getModalSubtitle = () => {
    switch (mode) {
      case "login":
        return "Please sign in to continue";
      case "register":
        return "Fill out the form below to get started";
      case "forgotPassword":
        return "Enter your email and phone to reset password";
      default:
        return "";
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay with blur effect */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      <div className="flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="relative bg-white rounded-2xl w-full max-w-md sm:max-w-lg shadow-2xl transform transition-all">
          {/* Close Button */}
          <button
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-50 
             w-10 h-10 flex items-center justify-center 
             rounded-full 
             text-gray-400 hover:text-[#d4a017] 
             hover:bg-gray-50 
             transition-all duration-200 
             focus:outline-none focus:ring-2 focus:ring-[#d4a017]/40"
          >
            <X size={20} />
          </button>

          {/* Header with brand gradient background */}
          <div className="bg-gradient-to-r from-[#02323F] to-[#035264] rounded-t-2xl px-6 py-4 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-20 h-20 bg-white/5 rounded-full -translate-x-10 -translate-y-10"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-white/5 rounded-full translate-x-8 translate-y-8"></div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white relative z-10">
              {getModalTitle()}
            </h1>
            <p className="text-white/80 text-sm sm:text-base mt-2 relative z-10">
              {getModalSubtitle()}
            </p>
          </div>

          {/* Form Container */}
          <div className="px-6 py-4">
            <form className="space-y-3" onSubmit={handleSubmit}>
              {mode === "login" && (
                <>
                  {step === 1 && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Email or Phone Number
                      </label>

                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>

                        <input
                          type="text"
                          name="identifier"
                          value={formData.identifier}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                          placeholder="Enter email or phone"
                        />
                      </div>

                      {errors.identifier && (
                        <p className="text-red-500 text-sm">
                          {errors.identifier}
                        </p>
                      )}
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Set Your Password
                      </label>

                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>

                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] outline-none"
                          placeholder="Set your password"
                        />

                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? (
                            <EyeOff size={20} />
                          ) : (
                            <Eye size={20} />
                          )}
                        </button>
                        {errors.password && (
                          <p className="text-red-500 text-sm">
                            {errors.password}
                          </p>
                        )}
                      </div>

                      <label className="block text-sm font-semibold text-gray-700">
                        Confirm Your Password
                      </label>

                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>

                        <input
                          type={showPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] outline-none"
                          placeholder="Confirm your password"
                        />

                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? (
                            <EyeOff size={20} />
                          ) : (
                            <Eye size={20} />
                          )}
                        </button>

                        {errors.confirmPassword && (
                          <p className="text-red-500 text-sm">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {mode === "register" && (
                <>
                  <div className="flex flex-col max-w-5xl gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                          placeholder="Your name"
                        />
                      </div>
                      {errors.name && (
                        <p className="text-red-500 text-sm flex items-center gap-1">
                          <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Gender
                      </label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div> */}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                        placeholder="1234567890"
                      />
                    </div>
                    {errors.phoneNumber && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Date of Birth
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Calendar className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                        />
                      </div>
                    </div> */}

                    {/* <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Nationality
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Flag className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                          placeholder="Your nationality"
                        />
                      </div>
                    </div> */}
                  </div>

                  {/* <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-5 w-5 text-gray-400" />
                      </div>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors resize-none outline-none"
                        placeholder="Your address"
                      />
                    </div>
                  </div> */}
                </>
              )}

              {mode === "forgotPassword" && (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                        placeholder="1234567890"
                      />
                    </div>
                    {errors.phoneNumber && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      New Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#d4a017] transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                        {errors.newPassword}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Password field for login and register */}
              {mode === "register" && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-[#02323F] transition-colors outline-none"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#d4a017] transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                      {errors.password}
                    </p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#02323F] to-[#035264] hover:from-[#035264] hover:to-[#046276] text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg hover:shadow-[#d4a017]/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  <>
                    {mode === "login" && (
                      <>
                        {step === 1 && "Continue"}
                        {step === 2 && "Login"}
                      </>
                    )}
                    {mode === "register" && "Create Account"}
                    {mode === "forgotPassword" && "Reset Password"}
                  </>
                )}
              </button>
            </form>

            {/* Mode Switching */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center space-y-3">
                {mode === "login" && (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">
                      Don't have an account?{" "}
                      <button
                        type="button"
                        onClick={() => switchMode("register")}
                        className="text-[#02323F] hover:text-[#d4a017] font-semibold transition-colors"
                      >
                        Create one now
                      </button>
                    </div>
                    <div className="text-sm">
                      <button
                        type="button"
                        onClick={() => switchMode("forgotPassword")}
                        className="text-[#02323F] hover:text-[#d4a017] font-semibold transition-colors"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </div>
                )}

                {mode === "register" && (
                  <div className="text-sm text-gray-600">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode("login")}
                      className="text-[#02323F] hover:text-[#d4a017] font-semibold transition-colors"
                    >
                      Sign in here
                    </button>
                  </div>
                )}

                {mode === "forgotPassword" && (
                  <div className="text-sm text-gray-600">
                    Remember your password?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode("login")}
                      className="text-[#02323F] hover:text-[#d4a017] font-semibold transition-colors"
                    >
                      Back to login
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UserLoginModal;
